import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  clearLobby,
  sendChatMessage,
} from '@/store/slices/lobbySlice';

interface Props {
  /**
   * When false, the component renders nothing. The Watch page passes
   * `currentLobby != null` so the panel only appears in a lobby.
   */
  enabled: boolean;
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Renders the lobby chat panel. The WebSocket lifecycle now lives in
 * `useLobbyWs()` at the App level so the socket stays open regardless of
 * which route the user is on — this component is purely presentational.
 */
export function LobbyChat({ enabled }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentLobby = useAppSelector((s) => s.lobby.currentLobby);
  const chat = useAppSelector((s) => s.lobby.chat);
  const wsState = useAppSelector((s) => s.lobby.wsState);
  const throttledAt = useAppSelector((s) => s.lobby.throttledAt);
  const closedReason = useAppSelector((s) => s.lobby.closedReason);
  const disconnected = useAppSelector((s) => s.lobby.disconnected);

  const [draft, setDraft] = useState('');
  const [throttledVisible, setThrottledVisible] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Auto-scroll the chat list when new messages arrive.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chat.length]);

  // Throttled warning lives for ~2s.
  useEffect(() => {
    if (throttledAt == null) return;
    setThrottledVisible(true);
    const handle = window.setTimeout(() => setThrottledVisible(false), 2000);
    return () => window.clearTimeout(handle);
  }, [throttledAt]);

  const wsStatusLabel = useMemo(() => {
    if (disconnected) return t('lobby.chat.statusDisconnected');
    switch (wsState) {
      case 'open': return t('lobby.chat.statusConnected');
      case 'connecting': return t('lobby.chat.statusConnecting');
      case 'closed': return t('lobby.chat.statusReconnecting');
      default: return t('lobby.chat.statusIdle');
    }
  }, [wsState, disconnected, t]);

  if (!enabled || !currentLobby) return null;

  const inputDisabled = wsState !== 'open';

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    void dispatch(sendChatMessage({ text }));
    setDraft('');
  }

  return (
    <aside className="lobby-chat" aria-label={t('lobby.chat.panelLabel')}>
      <div className="lobby-chat__header">
        <span>{t('lobby.chat.header', { code: currentLobby.code })}</span>
        <span aria-label={t('lobby.chat.connectionStatus')}>{wsStatusLabel}</span>
      </div>

      {throttledVisible && (
        <div role="status" className="lobby-chat__warning">{t('lobby.chat.slowDown')}</div>
      )}

      {closedReason && (
        <div role="alert" className="lobby-chat__notice">
          {closedReason}
          <button
            type="button"
            className="btn-ghost"
            style={{ marginLeft: 8 }}
            onClick={() => dispatch(clearLobby())}
          >
            {t('lobby.chat.dismiss')}
          </button>
        </div>
      )}

      {disconnected && (
        <div role="alert" className="lobby-chat__notice">
          {t('lobby.chat.disconnectedNotice')}
        </div>
      )}

      <div className="lobby-chat__list" ref={listRef} data-testid="lobby-chat-list">
        {chat.map((m, idx) => (
          <div
            key={`${m.ts}-${idx}`}
            className={`lobby-chat__msg${m.kind === 'system' ? ' lobby-chat__msg--system' : ''}`}
            title={fmtTime(m.ts)}
          >
            {m.kind === 'chat' ? (
              <>
                <span className="lobby-chat__msg-name">{m.displayName || m.from}:</span>
                {m.text}
              </>
            ) : (
              <span>{m.text}</span>
            )}
          </div>
        ))}
      </div>

      <form className="lobby-chat__form" onSubmit={onSubmit}>
        <input
          type="text"
          aria-label={t('lobby.chat.messageLabel')}
          placeholder={inputDisabled ? t('lobby.chat.connecting') : t('lobby.chat.placeholder')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={inputDisabled}
          maxLength={256}
        />
        <button type="submit" aria-label={t('lobby.chat.sendLabel')} disabled={inputDisabled || !draft.trim()}>
          {t('lobby.chat.send')}
        </button>
      </form>
    </aside>
  );
}
